// Vertical-agnostic FILTER ENGINE.
// Operates on the NORMALIZED offer data of any vertical — it filters the data model,
// it does not merely hide cards after render. Sorting is strictly commission-blind:
// comparators only reference customer-facing fields (price, rating, duration, data...),
// never any commission/earnings field.
//
// A vertical declares a schema; this engine derives facets, applies filters, sorts, and
// (de)serializes state to the URL so filtered/sorted pages are shareable & refreshable.
//
// Filter types:
//   range  : numeric window [min,max]. accessor(o) -> number|null (null = no data → passes
//            filter, excluded from bounds so we never fabricate a value).
//   multi  : set membership. accessor(o) -> value | value[]  (arrays flatten).
//   toggle : boolean. predicate(o) -> bool. Facet hidden unless at least one item matches.

const PREFIX = 'f_';

const asArray = (v) => (Array.isArray(v) ? v : v === undefined || v === null || v === '' ? [] : [v]);

export function buildFacets(items, schema) {
  const facets = {};
  const list = items || [];
  for (const f of schema.filters) {
    if (f.type === 'range') {
      const nums = list.map(f.accessor).filter((n) => typeof n === 'number' && isFinite(n));
      if (nums.length === 0) { facets[f.key] = { available: false }; continue; }
      facets[f.key] = { available: true, min: Math.floor(Math.min(...nums)), max: Math.ceil(Math.max(...nums)) };
    } else if (f.type === 'multi') {
      const set = new Map();
      for (const o of list) for (const v of asArray(f.accessor(o))) if (v !== null && v !== undefined && v !== '') set.set(String(v), v);
      const options = Array.from(set.values());
      facets[f.key] = { available: options.length > 0, options: options.sort((a, b) => (typeof a === 'number' ? a - b : String(a).localeCompare(String(b)))) };
    } else if (f.type === 'toggle') {
      facets[f.key] = { available: list.some((o) => !!f.predicate(o)) };
    }
  }
  return facets;
}

export function defaultValues(schema, facets) {
  const v = {};
  for (const f of schema.filters) {
    const fc = facets[f.key];
    if (!fc?.available) continue;
    if (f.type === 'range') v[f.key] = { min: fc.min, max: fc.max };
    else if (f.type === 'multi') v[f.key] = [];
    else if (f.type === 'toggle') v[f.key] = false;
  }
  return v;
}

export function applyFilters(items, schema, values, facets) {
  return (items || []).filter((o) => {
    for (const f of schema.filters) {
      const fc = facets[f.key];
      if (!fc?.available) continue;
      const val = values[f.key];
      if (f.type === 'range') {
        const n = f.accessor(o);
        if (typeof n === 'number' && isFinite(n)) {
          if (n < val.min || n > val.max) return false;
        } // null → passes (unknown, not fabricated)
      } else if (f.type === 'multi') {
        if (val && val.length) {
          const ov = asArray(f.accessor(o)).map(String);
          if (!val.some((s) => ov.includes(String(s)))) return false;
        }
      } else if (f.type === 'toggle') {
        if (val && !f.predicate(o)) return false;
      }
    }
    return true;
  });
}

export function applySort(items, schema, sortKey) {
  const s = schema.sorts.find((x) => x.key === sortKey) || schema.sorts[0];
  const arr = [...(items || [])];
  if (s && s.cmp) arr.sort(s.cmp);
  return arr;
}

export function activeCount(schema, values, facets) {
  let n = 0;
  for (const f of schema.filters) {
    const fc = facets[f.key];
    if (!fc?.available) continue;
    const val = values[f.key];
    if (f.type === 'range') { if (val && (val.min > fc.min || val.max < fc.max)) n++; }
    else if (f.type === 'multi') { if (val && val.length) n++; }
    else if (f.type === 'toggle') { if (val) n++; }
  }
  return n;
}

// ---- URL state (namespaced with f_ so other query params like destination_code survive) ----
export function decodeFromParams(searchParams, schema, facets) {
  const values = defaultValues(schema, facets);
  for (const f of schema.filters) {
    const fc = facets[f.key];
    if (!fc?.available) continue;
    const raw = searchParams.get(PREFIX + f.key);
    if (raw === null) continue;
    if (f.type === 'range') {
      const [a, b] = raw.split('-').map(Number);
      if (isFinite(a) && isFinite(b)) values[f.key] = { min: Math.max(fc.min, a), max: Math.min(fc.max, b) };
    } else if (f.type === 'multi') {
      const allowed = new Set(fc.options.map(String));
      values[f.key] = raw.split(',').filter((x) => allowed.has(x));
    } else if (f.type === 'toggle') {
      values[f.key] = raw === '1';
    }
  }
  const sortKey = searchParams.get('sort') || schema.defaultSort;
  return { values, sortKey };
}

export function writeToParams(searchParams, schema, facets, values, sortKey) {
  const next = new URLSearchParams(searchParams);
  for (const f of schema.filters) next.delete(PREFIX + f.key);
  next.delete('sort');
  for (const f of schema.filters) {
    const fc = facets[f.key];
    if (!fc?.available) continue;
    const val = values[f.key];
    if (f.type === 'range') { if (val && (val.min > fc.min || val.max < fc.max)) next.set(PREFIX + f.key, `${val.min}-${val.max}`); }
    else if (f.type === 'multi') { if (val && val.length) next.set(PREFIX + f.key, val.join(',')); }
    else if (f.type === 'toggle') { if (val) next.set(PREFIX + f.key, '1'); }
  }
  if (sortKey && sortKey !== schema.defaultSort) next.set('sort', sortKey);
  return next;
}
